import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { translate } from '../../../base/i18n/functions';
import { areDebugOverlaysEnabled, isInterpreter } from '../../../base/user-role/functions';
const useStyles = makeStyles()(theme => ({
    docked: {
        color: theme.palette.text01, background: theme.palette.ui02,
        borderRadius: theme.shape.borderRadius, padding: theme.spacing(2), marginTop: theme.spacing(2),
        fontSize: 14, lineHeight: '22px'
    },
    container: {
        position: 'fixed',
        backgroundColor: 'rgba(26, 35, 50, 0.9)',
        border: '1px solid rgba(74, 158, 255, 0.3)',
        borderRadius: '12px',
        padding: '10px 12px',
        minWidth: '220px',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
        cursor: 'move',
        zIndex: 550,
        touchAction: 'none',
        userSelect: 'none',
        '@media (max-width: 720px)': {
            maxWidth: 'calc(100vw - 32px)',
            minWidth: '0'
        }
    },
    // Production mode: the panel is an overlay, not furniture. It sits
    // faded over the video and only takes full opacity under the pointer.
    quietOverlay: {
        opacity: 0.35,
        transition: 'opacity 0.25s ease',
        '&:hover, &:focus-within': {
            opacity: 1
        }
    },
    quietChip: {
        minWidth: 0,
        padding: '8px 10px'
    },
    waitingBadge: {
        backgroundColor: '#FF6B35',
        borderRadius: '9px',
        color: '#fff',
        fontSize: '11px',
        fontWeight: 700,
        lineHeight: '18px',
        minWidth: '18px',
        padding: '0 5px',
        textAlign: 'center'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 0,
        paddingBottom: 0,
        cursor: 'move',
        gap: '12px'
    },
    title: {
        color: '#ffffff',
        fontSize: '13px',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
    },
    collapseButton: {
        background: 'none',
        border: 'none',
        color: 'rgba(255, 255, 255, 0.6)',
        cursor: 'pointer',
        fontSize: '16px',
        padding: '4px',
        '&:hover': {
            color: 'white'
        }
    },
    stats: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        marginTop: '12px',
        paddingTop: '10px',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
    },
    collapsed: {
        display: 'none'
    },
    statRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    statLabel: {
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '12px'
    },
    statValue: {
        color: 'white',
        fontSize: '14px',
        fontWeight: 'bold'
    },
    onlineStat: {
        color: '#28a745'
    },
    waitingStat: {
        color: '#FF6B35'
    },
    connectedIndicator: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '11px',
        color: 'rgba(255, 255, 255, 0.6)',
        marginTop: '8px',
        paddingTop: '8px',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
    },
    dot: {
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        backgroundColor: '#28a745'
    },
    dotDisconnected: {
        backgroundColor: '#dc3545'
    }
}));
const DEFAULT_POSITION = { x: 16, y: 104 };
const MIN_EDGE_GAP = 8;
const POSITION_STORAGE_KEY = 'vrs_queue_stats_position';
function getStoredPosition() {
    if (typeof localStorage === 'undefined') {
        return DEFAULT_POSITION;
    }
    try {
        const storedPosition = JSON.parse(localStorage.getItem(POSITION_STORAGE_KEY) || '');
        if (Number.isFinite(storedPosition?.x) && Number.isFinite(storedPosition?.y)) {
            return {
                x: storedPosition.x,
                y: storedPosition.y
            };
        }
    }
    catch {
        // Ignore stale or malformed persisted panel positions.
    }
    return DEFAULT_POSITION;
}
/**
 * Queue stats dashboard for interpreters.
 * Shows interpreter count, pending requests, and connection status.
 */
const QueueStatsDashboard = ({ _queueState, t, docked = false }) => {
    const { classes } = useStyles();
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [position, setPosition] = useState(getStoredPosition);
    const dragStateRef = useRef(null);
    const containerRef = useRef(null);
    const { isConnected, activeInterpreters, pendingRequests } = _queueState;
    const clampPosition = useCallback((nextX, nextY, width, height) => {
        const viewportWidth = typeof window === 'undefined' ? 1024 : window.innerWidth;
        const viewportHeight = typeof window === 'undefined' ? 768 : window.innerHeight;
        const currentElement = containerRef.current;
        const panelWidth = width || currentElement?.offsetWidth || 220;
        const panelHeight = height || currentElement?.offsetHeight || 54;
        return {
            x: Math.min(Math.max(nextX, MIN_EDGE_GAP), Math.max(MIN_EDGE_GAP, viewportWidth - panelWidth - MIN_EDGE_GAP)),
            y: Math.min(Math.max(nextY, MIN_EDGE_GAP), Math.max(MIN_EDGE_GAP, viewportHeight - panelHeight - MIN_EDGE_GAP))
        };
    }, []);
    const handleDragMove = useCallback((event) => {
        const dragState = dragStateRef.current;
        if (!dragState) {
            return;
        }
        setPosition(clampPosition(event.clientX - dragState.offsetX, event.clientY - dragState.offsetY, dragState.width, dragState.height));
    }, [clampPosition]);
    const handleDragEnd = useCallback(() => {
        dragStateRef.current = null;
        window.removeEventListener('pointermove', handleDragMove);
        window.removeEventListener('pointerup', handleDragEnd);
        window.removeEventListener('pointercancel', handleDragEnd);
    }, [handleDragMove]);
    useEffect(() => {
        const handleResize = () => {
            setPosition(current => clampPosition(current.x, current.y));
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('pointermove', handleDragMove);
            window.removeEventListener('pointerup', handleDragEnd);
            window.removeEventListener('pointercancel', handleDragEnd);
        };
    }, [clampPosition, handleDragEnd, handleDragMove]);
    useEffect(() => {
        if (typeof localStorage === 'undefined') {
            return;
        }
        localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position));
    }, [position]);
    useEffect(() => {
        requestAnimationFrame(() => {
            setPosition(current => clampPosition(current.x, current.y));
        });
    }, [clampPosition, isCollapsed]);
    const handleDragStart = useCallback((event) => {
        if (event.button !== 0 || event.target.closest?.('button')) {
            return;
        }
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) {
            return;
        }
        dragStateRef.current = {
            height: rect.height,
            offsetX: event.clientX - rect.left,
            offsetY: event.clientY - rect.top,
            width: rect.width
        };
        event.currentTarget.setPointerCapture?.(event.pointerId);
        window.addEventListener('pointermove', handleDragMove);
        window.addEventListener('pointerup', handleDragEnd);
        window.addEventListener('pointercancel', handleDragEnd);
        event.preventDefault();
    }, [handleDragEnd, handleDragMove]);
    // Don't render if not an interpreter
    if (!isInterpreter()) {
        return null;
    }
    const debugOverlays = areDebugOverlaysEnabled();
    const waitingCount = pendingRequests?.length || 0;
    if (docked) {
        return React.createElement("div", { className: classes.docked, role: 'status' },
            React.createElement("div", null, isConnected ? 'Queue connected' : 'Queue disconnected'),
            isConnected && React.createElement("div", null, `${waitingCount} waiting · ${activeInterpreters || 0} interpreters available`));
    }
    // Without the debug permission, the collapsed panel is just a quiet
    // chip: connection dot plus a waiting-count badge when the queue has
    // work. Expanding it still shows the full numbers on demand.
    if (!debugOverlays && isCollapsed) {
        return (React.createElement("div", { className: `${classes.container} ${classes.quietOverlay} ${classes.quietChip}`, onPointerDown: handleDragStart, ref: containerRef, style: { left: position.x, right: 'auto', top: position.y } },
            React.createElement("button", { "aria-label": `Queue: ${isConnected ? 'connected' : 'disconnected'}, ${waitingCount} waiting. Expand.`, className: classes.collapseButton, onClick: () => setIsCollapsed(false), style: { alignItems: 'center', display: 'flex', gap: '6px', padding: 0 } },
                React.createElement("span", { className: `${classes.dot} ${!isConnected ? classes.dotDisconnected : ''}` }),
                waitingCount > 0 && React.createElement("span", { className: classes.waitingBadge }, waitingCount))));
    }
    return (React.createElement("div", { className: `${classes.container} ${debugOverlays ? '' : classes.quietOverlay}`, onPointerDown: handleDragStart, ref: containerRef, style: { left: position.x, right: 'auto', top: position.y } },
        React.createElement("div", { className: classes.header },
            React.createElement("span", { className: classes.title },
                "\uD83D\uDCCA ",
                t('vrs.queueStats')),
            React.createElement("button", { className: classes.collapseButton, onClick: () => setIsCollapsed(!isCollapsed) }, isCollapsed ? '◀' : '▼')),
        React.createElement("div", { className: `${classes.stats} ${isCollapsed ? classes.collapsed : ''}` },
            React.createElement("div", { className: classes.statRow },
                React.createElement("span", { className: classes.statLabel }, t('vrs.interpretersOnline')),
                React.createElement("span", { className: `${classes.statValue} ${classes.onlineStat}` }, activeInterpreters || 0)),
            React.createElement("div", { className: classes.statRow },
                React.createElement("span", { className: classes.statLabel }, t('vrs.requestsWaiting')),
                React.createElement("span", { className: `${classes.statValue} ${classes.waitingStat}` }, pendingRequests?.length || 0)),
            React.createElement("div", { className: classes.statRow },
                React.createElement("span", { className: classes.statLabel }, t('vrs.yourStatus')),
                React.createElement("span", { className: classes.statValue }, isConnected ? t('vrs.online') : t('vrs.offline')))),
        React.createElement("div", { className: `${classes.connectedIndicator} ${isCollapsed ? classes.collapsed : ''}` },
            React.createElement("span", { className: `${classes.dot} ${!isConnected ? classes.dotDisconnected : ''}` }),
            React.createElement("span", null, isConnected ? t('vrs.connectedToQueue') : t('vrs.disconnectedFromQueue')))));
};
function _mapStateToProps(state) {
    return {
        _queueState: state['features/interpreter-queue']
    };
}
export default translate(connect(_mapStateToProps)(QueueStatsDashboard));
